<template>
  <!-- 1 物件（1 区画）＝ 1 つの tbody。押した欄だけ小窓で編集し、行の「保存」でその物件のマスタをまとめて保存する -->
  <tbody>
    <tr class="row" :class="[dirty ? 'row-dirty' : '', expanded ? 'row-open' : '']">

      <!-- 物件・得意先 -->
      <td class="site-cell">
        <div class="flex items-center flex-wrap">
          <span class="site-name">{{ row.site }}</span>
          <span v-if="saving" class="badge badge-blue">保存中…</span>
          <span v-else-if="dirty" class="badge badge-yellow">未保存</span>
          <span v-else-if="savedMark" class="badge badge-green">保存しました</span>
          <span v-else-if="!row.startDate" class="badge badge-orange">未設定</span>
          <span v-if="special && special.format" class="badge badge-pink">特別仕様</span>
        </div>
        <div class="site-client">{{ row.client }}</div>
        <!-- 概要書・カレンダー・その他の設定（1 行に収めて行を高くしない） -->
        <div class="flex items-center mt-2" style="gap: 4px">
          <button type="button" class="pill" @click="$emit('outline', row.siteCode)">概要書</button>
          <button v-if="row.startDate" type="button" class="pill" @click="$emit('calendar', row)">カレンダー</button>
          <button v-if="editable" type="button" class="more ml-auto" :class="expanded ? 'more-open' : ''" @click="toggleExpand()">
            {{ expanded ? '閉じる' : 'その他の設定' }}
            <ChevronUpIcon v-if="expanded" class="w-4 h-4 ml-1" />
            <ChevronDownIcon v-else class="w-4 h-4 ml-1" />
          </button>
        </div>
      </td>

      <!-- 実施月 -->
      <td class="center-cell">
        <div class="inline-block box" :class="editable ? 'box-edit' : ''" @click="openPopover('months', null, null, $event)">
          {{ view.months || '―' }}
        </div>
      </td>

      <!-- 曜日ごと（上：ゴミ出し、下：清掃） -->
      <td v-for="w in weekList" :key="w.id" class="week-cell">
        <div class="flex flex-col" style="gap: 6px">
          <div v-for="t in types" :key="t">
            <!-- 札 -->
            <div v-if="view[t + w.id].length" class="chip" :class="[t === 'trash' ? 'chip-trash' : 'chip-clean', editable ? 'chip-edit' : '', isPreset(t, w.id) ? 'chip-preset' : '']" @click="openPopover('att', t, w.id, $event)">
              <TrashSvg v-if="t === 'trash'" />
              <BroomSvg v-else />
              <span class="chip-att">{{ view[t + w.id].join('・') }}</span>
              <span v-if="view[t + 'Staff' + w.id]" class="chip-staff">{{ view[t + 'Staff' + w.id] }}</span>
              <span v-else class="chip-staff chip-none">未定</span>
            </div>
            <!-- 空 -->
            <div v-else class="chip-empty" :class="editable ? 'chip-edit' : ''" @click="openPopover('att', t, w.id, $event)">
              <span v-if="editable">＋</span>
            </div>
          </div>
          <!-- 振替先（祝日が「振替」のときだけ） -->
          <div v-if="view.holiday === '振替' && view['clean' + w.id].length" class="exchange" :class="editable ? 'box-edit' : ''" @click="openPopover('exchange', 'clean', w.id, $event)">
            振替先 {{ view['exchange' + w.id] || '指定なし' }}
          </div>
          <!-- 曜日ごとのコピーと貼付（ゴミ出し・清掃の形態と担当者だけ。振替先は写さない） -->
          <div v-if="editable" class="flex justify-center" style="gap: 12px">
            <button type="button" class="cp-btn" :class="clipWeek === w.id ? 'cp-src' : ''" @click="copyWeek(w)">{{ clipWeek === w.id ? 'コピー元' : 'コピー' }}</button>
            <button type="button" class="cp-btn" :disabled="!clip || clipWeek === w.id" @click="pasteWeek(w)">貼付</button>
          </div>
        </div>
      </td>

      <!-- 祝日（自動判定） -->
      <td class="center-cell">
        <span v-if="view.holiday === '振替'" class="badge badge-indigo">振替</span>
        <span v-else-if="view.holiday === '清掃なし'" class="badge badge-gray">清掃なし</span>
        <span v-else class="text-gray-400">―</span>
      </td>

      <!-- 適用開始日 -->
      <td class="center-cell">
        <div v-if="active === 'start'" class="w-40 mx-auto">
          <WmsDateField :value="work.startDate" :clearable="false" @change="changeStart" />
        </div>
        <div v-else class="inline-block box" :class="canEditStart ? 'box-edit' : ''" @click="openStart()">
          {{ view.startDate || '―' }}
        </div>
        <div v-if="view.endDate" class="text-xs text-gray-500">（{{ view.endDate }}まで）</div>
      </td>

      <!-- 保存・取消 -->
      <td class="text-right whitespace-nowrap action-cell">
        <div v-if="dirty && !saving" class="flex flex-col items-center" style="gap: 8px">
          <button type="button" class="save-btn" @click="save()">保存</button>
          <button type="button" class="cancel-btn" @click="cancel()">取消</button>
        </div>

        <!-- 小窓（形態と担当者・実施月・振替先）。画面に固定で出す -->
        <div v-if="popover.kind" ref="popover" class="popover text-left" :style="{ top: popover.y + 'px', left: popover.x + 'px' }">
          <!-- 形態と担当者 -->
          <div v-if="popover.kind === 'att'">
            <div class="pop-title mb-2">{{ popover.type === 'trash' ? 'ゴミ出し' : '清掃' }}&#12288;{{ weekName(popover.week) }}曜日</div>
            <div class="grid grid-cols-4 gap-1">
              <button v-for="a in attendanceList" :key="a.id" type="button" class="opt" :class="work && work[popover.type + popover.week].includes(a.id) ? 'opt-on' : ''" @click="toggleAtt(a.id)">{{ a.name }}</button>
              <button type="button" class="opt opt-off" :disabled="!work || !work[popover.type + popover.week].length" @click="clearAtt()">なし</button>
            </div>
            <div class="mt-3 pt-3 border-t border-gray-100">
              <label class="pop-title block mb-1">担当者</label>
              <WmsSearchSelect
                name="rlsMstStaff"
                :value="staffValue"
                :options="staffList"
                track-by="userId"
                label="userName"
                placeholder="名前で検索"
                :clearable="true"
                :disabled="!work || !work[popover.type + popover.week].length"
                @input="staffSelected"
              />
              <div v-if="work && !work[popover.type + popover.week].length" class="text-xs text-gray-400 mt-1">形態を選ぶと担当者を入れられます</div>
            </div>
            <div class="text-xs text-gray-500 mt-2">「毎週」「隔週」はほかと一緒に選べません</div>
          </div>
          <!-- 実施月 -->
          <div v-else-if="popover.kind === 'months'">
            <div class="pop-title mb-2">実施月</div>
            <div class="grid grid-cols-6 gap-1">
              <button v-for="m in 12" :key="m" type="button" class="opt" :class="work && work.months.includes(m) ? 'opt-on' : ''" @click="toggleMonth(m)">{{ m }}月</button>
            </div>
          </div>
          <!-- 振替先 -->
          <div v-else-if="popover.kind === 'exchange'">
            <div class="pop-title mb-2">{{ weekName(popover.week) }}曜日が祝日のときの振替先</div>
            <div class="grid grid-cols-4 gap-1">
              <button type="button" class="opt" :class="work && !work['exchange' + popover.week] ? 'opt-on' : ''" @click="setExchange(null)">指定なし</button>
              <button v-for="w in weekList" :key="w.id" type="button" class="opt" :class="work && work['exchange' + popover.week] === w.num ? 'opt-on' : ''" :disabled="w.id === popover.week" @click="setExchange(w.num)">{{ w.name }}</button>
            </div>
            <div class="text-xs text-gray-500 mt-2">指定なしはシステムが自動で振替</div>
          </div>
          <div class="text-right mt-3">
            <SecondaryButton text="閉じる" size="xs" @click="closePopover()" />
          </div>
        </div>
      </td>
    </tr>

    <!-- その他の設定（たまにしか触らないもの） -->
    <tr v-if="expanded" class="row-more">
      <td :colspan="weekList.length + 5">
        <div v-if="!work" class="text-sm text-gray-500 p-3">読み込み中…</div>
        <div v-else class="flex items-stretch p-3" style="gap: 16px">
          <!-- 新しい区画 -->
          <div class="card flex-1">
            <div class="card-title">新しい設定を始める</div>
            <div class="card-note">今の内容を写して、入れた日から新しい設定にします。前の設定はその前日までになります。</div>
            <div class="flex items-end mt-2" style="gap: 8px">
              <WmsDateField class="w-40" caption="適用開始日" :value="newStart" :min="newStartMin" :clearable="false" @change="newStart = $event" />
              <PrimaryButton text="追加" size="sm" :buttonContents="'要'" @click="addPeriod()" />
            </div>
          </div>

          <!-- 区画の一覧 -->
          <div class="card flex-1">
            <div class="card-title">設定の区画</div>
            <div class="flex flex-col mt-2 text-xs" style="gap: 6px">
              <div v-for="(p, i) in works" :key="'p' + i" class="period" :class="p === work ? 'period-cur' : ''">
                <span>{{ p.startDate || '（日付なし）' }} 〜 {{ p.endDate || '' }}</span>
                <span v-if="p === work" class="period-mark">この行</span>
                <TrashIcon v-if="works.length > 1 && p !== work && p.startDate > today" class="w-4 h-4 ml-auto text-gray-400 cursor-pointer hover:text-red-600" title="この区画を削除" @click="removePeriod(i)" />
              </div>
            </div>
          </div>

          <!-- 特別仕様（得意先ごとの独自フォーマットの報告書） -->
          <div v-if="spec && showSpec" class="card" style="width: 340px">
            <div class="card-title">特別仕様</div>
            <div class="card-note">得意先の指定の書式で出す報告書。仕様を選ぶと、巡回清掃の報告書で「通常／特別仕様／両方」を選べるようになります。</div>
            <div class="flex items-center mt-2" style="gap: 8px">
              <label for="specFormat" class="text-xs text-gray-600">仕様</label>
              <select id="specFormat" v-model="spec.format" class="spec-select" @change="changeFormat">
                <option v-for="f in formatOptions" :key="f.code" :value="f.code">{{ f.name }}</option>
              </select>
            </div>
            <div v-if="specDef" class="mt-2">
              <div class="text-xs text-gray-600 mb-1">{{ specDef.note }}。箇所は {{ specDef.itemCount }} つ固定（空の箇所は物件ごとに決める。現場で付けた名前は次回に引き継がれます）</div>
              <draggable :list="spec.items" tag="div" handle=".spec-handle" direction="vertical">
                <div v-for="(it, i) in spec.items" :key="it.key" class="flex items-center mb-1" style="gap: 6px">
                  <div class="spec-handle text-gray-400 cursor-grab flex-none"><MenuIcon class="w-4 h-4" /></div>
                  <span class="text-xs text-gray-500 w-4 text-right flex-none">{{ i + 1 }}</span>
                  <input v-model="it.name" type="text" class="spec-input" placeholder="箇所名（空でも可）" @input="specDirty = true" />
                </div>
              </draggable>
            </div>
          </div>

          <!-- 削除 -->
          <div v-if="row.startDate" class="flex flex-col justify-end" style="width: 160px">
            <button type="button" class="danger" @click="deleteMst()">マスタを削除</button>
          </div>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import WmsSearchSelect from '@wmscomponents/WmsSearchSelect.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import SecondaryButton from '@components/SecondaryButton.vue'
import { TrashIcon, ChevronDownIcon, ChevronUpIcon, MenuIcon } from '@vue-hero-icons/solid'
import draggable from 'vuedraggable'
import { RLS_SPECIAL_FORMATS } from '@libs/constants'
import * as logManager from '@managers/logManager'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import * as backend from '@libs/backend'
import * as moment from 'moment'
import BigNumber from 'bignumber.js'

// 札の中の小さな絵（ゴミ袋・ほうき）
const TrashSvg = {
  render (h) {
    return h('svg', { attrs: { 'aria-hidden': 'true', width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, class: 'flex-none' }, [
      h('path', { attrs: { d: 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14' } })
    ])
  }
}
// ほうき（立てた形）：まっすぐの柄・下に広がる台形の穂先（薄く塗る）・穂の筋 3 本・下の端
const BroomSvg = {
  render (h) {
    return h('svg', { attrs: { 'aria-hidden': 'true', width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, class: 'flex-none' }, [
      // 柄
      h('path', { attrs: { d: 'M12 2v8' } }),
      // 穂先（台形）
      h('path', { attrs: { d: 'M8.5 10h7l3.5 11h-14Z', fill: 'currentColor', 'fill-opacity': '0.3' } }),
      // 穂の筋
      h('path', { attrs: { d: 'M9.5 14 8.5 21M12 14v7M14.5 14l1 7', 'stroke-width': 1.3 } })
    ])
  }
}

// 曜日（マスタの列名の末尾と同じ）
const WEEKS = [
  { id: 'Mon', name: '月', num: 1 },
  { id: 'Tue', name: '火', num: 2 },
  { id: 'Wed', name: '水', num: 3 },
  { id: 'Thu', name: '木', num: 4 },
  { id: 'Fri', name: '金', num: 5 },
  { id: 'Sat', name: '土', num: 6 }
]

// 勤務形態（マスタには id を入れる）
const ATTENDANCE = [
  { id: '00', name: '毎週' },
  { id: '01', name: '第1' },
  { id: '02', name: '第2' },
  { id: '03', name: '第3' },
  { id: '04', name: '第4' },
  { id: '05', name: '第5' },
  { id: '10', name: '隔週' }
]

// 巡回清掃の月 1・2 回の物件は「毎週」「隔週」を選べない
const ATTENDANCE_BY_MONTH = ATTENDANCE.filter((a) => !['00', '10'].includes(a.id))

/**
 * 区画（適用開始日ごとの設定）の空の形。キーは最初からそろえておく
 */
function emptyWork () {
  const w = {
    startDate: moment().format('YYYY-MM-01'),
    endDate: null,
    months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    holiday: null
  }
  for (const week of WEEKS) {
    for (const t of ['trash', 'clean']) {
      w[t + week.id] = []
      w[t + 'Staff' + week.id] = ''
      w[t + 'StaffId' + week.id] = null
    }
    w['exchange' + week.id] = null
  }
  return w
}

/**
 * API の行（getRlsMstBySiteCode）を区画の形にそろえる
 */
function toWork (r) {
  const w = emptyWork()
  w.startDate = r.startDate || null
  w.endDate = r.endDate || null
  w.months = Array.isArray(r.months) ? r.months.map((m) => Number(m)) : []
  w.holiday = r.holiday == null ? null : Number(r.holiday)
  for (const week of WEEKS) {
    for (const t of ['trash', 'clean']) {
      w[t + week.id] = Array.isArray(r[t + week.id]) ? r[t + week.id].slice() : []
      w[t + 'Staff' + week.id] = r[t + 'Staff' + week.id] || ''
      // 担当者 id は API が文字列で返すことがあるので数値にそろえる（候補の一覧と突き合わせるため）
      w[t + 'StaffId' + week.id] = r[t + 'StaffId' + week.id] ? Number(r[t + 'StaffId' + week.id]) : null
    }
    w['exchange' + week.id] = r['exchange' + week.id] == null ? null : Number(r['exchange' + week.id])
  }
  return w
}

export default {
  components: {
    WmsSearchSelect,
    WmsDateField,
    PrimaryButton,
    SecondaryButton,
    TrashIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MenuIcon,
    draggable,
    TrashSvg,
    BroomSvg
  },

  props: {
    // 一覧の行（getRlsMst の 1 行＝1 物件の 1 区画）
    row: {
      type: Object,
      required: true
    },
    // 担当者の候補（固定休 holidayWeeks 付き）
    staffList: {
      type: Array,
      default: () => []
    },
    // 編集できる権限があるか
    editable: {
      type: Boolean,
      default: false
    },
    // スタッフごとタブから別タブで開いたときに最初に入れる担当（{ type, week, staffId, staff }）
    preset: {
      type: Object,
      default: null
    },
    // この物件の特別仕様（{ siteCode, format, items }。無ければ null）
    special: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      weekList: WEEKS,
      types: ['trash', 'clean'],
      // この物件の区画（適用開始日の新しい順）。最初に欄を押したときに取る
      works: null,
      // この行が表す区画
      work: null,
      // 勤務形態の候補
      attendanceList: ATTENDANCE,
      // 未保存の変更があるか（マスタ＝予定の作り直しを伴う／特別仕様＝伴わない）
      mstDirty: false,
      specDirty: false,
      saving: false,
      savedMark: false,
      // 読み込み中
      loading: false,
      // 編集中の欄（適用開始日）
      active: null,
      // 曜日のコピー（ゴミ出し・清掃の形態と担当者）とコピー元の曜日
      clip: null,
      clipWeek: null,
      // 小窓（形態と担当者・実施月・振替先）
      popover: { kind: null, type: null, week: null, x: 0, y: 0 },
      // その他の設定の開閉
      expanded: false,
      // 新しい区画の開始日
      newStart: null,
      // 区画を足した・消した（保存後に一覧を取り直す）
      structureChanged: false,
      // スタッフごとタブから入れた欄の印（保存したら消す）
      presetMark: true,
      // 特別仕様の編集中の値（{ format, items: [{ key, name }] }）。その他の設定を開いたときに prop から写す
      spec: null,
      specialFormats: RLS_SPECIAL_FORMATS,
      today: moment().format('YYYY-MM-DD')
    }
  },

  computed: {
    /**
     * 未保存の変更があるか
     */
    dirty () {
      return this.mstDirty || this.specDirty
    },

    /**
     * この物件の得意先に出せる仕様のコード（定数の clients に得意先名が当たるもの。clients が空なら全部）
     */
    availableFormats () {
      const client = utils.zenkaku(this.row.client || '').replace(/\s/g, '')
      return Object.keys(RLS_SPECIAL_FORMATS).filter((code) => {
        const clients = RLS_SPECIAL_FORMATS[code].clients || []
        return !clients.length || clients.some((c) => client.indexOf(utils.zenkaku(c)) > -1)
      })
    },

    /**
     * 特別仕様のカードを出すか（出せる仕様がある得意先、または既に設定がある物件）
     */
    showSpec () {
      return this.availableFormats.length > 0 || !!(this.special && this.special.format)
    },

    /**
     * 仕様の選択肢（なし＋出せる仕様。既に設定済みの仕様は得意先が当たらなくても残す）
     */
    formatOptions () {
      const codes = this.availableFormats.slice()
      if (this.special && this.special.format && !codes.includes(this.special.format)) {
        codes.push(this.special.format)
      }
      return [{ code: '', name: 'なし' }].concat(codes.map((code) => ({ code, name: RLS_SPECIAL_FORMATS[code].name })))
    },

    /**
     * 今選んでいる仕様の定義
     */
    specDef () {
      return this.spec && this.spec.format ? RLS_SPECIAL_FORMATS[this.spec.format] : null
    },

    /**
     * 表示用の値。区画を読み込んでいればそこから、まだなら一覧の行から
     */
    view () {
      const v = {}
      if (this.work) {
        const w = this.work
        v.months = w.months.length === 12 ? '毎月' : (w.months.length ? w.months.slice().sort((a, b) => a - b).join('・') + '月' : '')
        v.holiday = w.holiday === 1 ? '振替' : (w.holiday === 2 ? '清掃なし' : '')
        v.startDate = w.startDate
        v.endDate = w.endDate
        for (const week of WEEKS) {
          for (const t of this.types) {
            v[t + week.id] = w[t + week.id].map((id) => this.attName(id))
            v[t + 'Staff' + week.id] = w[t + 'Staff' + week.id]
          }
          const ex = WEEKS.find((x) => x.num === w['exchange' + week.id])
          v['exchange' + week.id] = ex ? ex.name : ''
        }
      } else {
        const r = this.row
        v.months = r.months
        v.holiday = r.holiday
        v.startDate = r.startDate
        v.endDate = r.endDate
        for (const week of WEEKS) {
          for (const t of this.types) {
            const n = r[t + week.id + 'N']
            v[t + week.id] = n ? n.split('・') : []
            v[t + 'Staff' + week.id] = r[t + 'Staff' + week.id] || ''
          }
          v['exchange' + week.id] = r['exchange' + week.id] || ''
        }
      }
      return v
    },

    /**
     * 適用開始日を変えられるのは一番新しい区画だけ
     */
    canEditStart () {
      if (!this.editable) {
        return false
      }
      if (!this.work) {
        return true
      }
      return this.works[0] === this.work
    },

    /**
     * 小窓で開いている曜日の担当者（候補の中のオブジェクト。無ければ null）
     */
    staffValue () {
      if (this.popover.kind !== 'att' || !this.work) {
        return null
      }
      const id = this.work[this.popover.type + 'StaffId' + this.popover.week]
      if (!id) {
        return null
      }
      return this.staffList.find((s) => Number(s.userId) === Number(id)) || null
    },

    /**
     * 新しい区画の開始日の下限（今の一番新しい区画の翌日）
     */
    newStartMin () {
      if (this.works && this.works[0] && this.works[0].startDate) {
        return moment(this.works[0].startDate + 'T00:00:00').add(1, 'days').format('YYYY-MM-DD')
      }
      return null
    }
  },

  watch: {
    dirty (val) {
      this.emitDirty(val)
    }
  },

  async mounted () {
    document.addEventListener('mousedown', this.onDocumentDown)
    if (this.preset && this.editable) {
      await this.applyPreset()
    }
  },

  beforeDestroy () {
    document.removeEventListener('mousedown', this.onDocumentDown)
  },

  methods: {
    /**
     * 未保存の状態を親に知らせる
     * （watch は次の描画まで待つので、保存・取消の直後に一覧を取り直すときは先にこれを呼んで「未保存あり」の確認が出ないようにする）
     */
    emitDirty (val) {
      this.$emit('dirty', { siteCode: this.row.siteCode, startDate: this.row.startDate, dirty: val })
    },

    /**
     * 曜日名
     */
    weekName (id) {
      const w = WEEKS.find((x) => x.id === id)
      return w ? w.name : ''
    },

    /**
     * 形態 id → 名前
     */
    attName (id) {
      const a = ATTENDANCE.find((x) => x.id === id)
      return a ? a.name : id
    },

    /**
     * スタッフごとタブから入れた欄か（黄色の枠）
     */
    isPreset (type, week) {
      return !!(this.presetMark && this.preset && this.preset.type === type && this.preset.week === week)
    },

    /**
     * この物件の区画を取る（最初の操作のときだけ）
     * API: rls/getRlsMstBySiteCode・rls/isByMonth
     */
    async ensureLoaded () {
      if (this.works) {
        return true
      }
      if (this.loading) {
        return false
      }
      this.loading = true
      try {
        const res = await backend.searchData('rls/getRlsMstBySiteCode', { siteCode: this.row.siteCode })
        const rows = (res.data && res.data.data) || []
        let works = []
        // 登録済み（区画が 2 つ以上、または 1 つ目に実施月がある）
        if (rows.length > 1 || (rows[0] && rows[0].months)) {
          works = rows.map((r) => toWork(r))
        }
        // 未設定なら今月 1 日からの空の区画を 1 つ
        if (!works.length) {
          works = [emptyWork()]
        }
        this.works = works
        // この行の区画（適用開始日が同じもの。無ければ一番新しいもの）
        this.work = works.find((w) => w.startDate === this.row.startDate) || works[0]

        // 巡回清掃の月 1・2 回の物件は毎週・隔週を選べない
        const res2 = await backend.searchData('rls/isByMonth', { siteCode: this.row.siteCode })
        this.attendanceList = (res2.data && res2.data.data) ? ATTENDANCE_BY_MONTH : ATTENDANCE
        return true
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', 'マスタを読み込めませんでした。')
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * スタッフごとタブから開いたときの担当を入れる（保存は人が押す）
     */
    async applyPreset () {
      if (!await this.ensureLoaded()) {
        return
      }
      const p = this.preset
      const key = p.type + p.week
      if (!this.work[key].length) {
        this.work[key] = [this.attendanceList[0].id]
      }
      this.work[p.type + 'Staff' + p.week] = p.staff
      this.work[p.type + 'StaffId' + p.week] = Number(p.staffId)
      if (p.type === 'clean') {
        this.setHolidayType(this.work)
      }
      this.mstDirty = true
    },

    // ---------- 小窓 ----------

    /**
     * 小窓を開く（形態と担当者・実施月・振替先）
     */
    async openPopover (kind, type, week, e) {
      if (!this.editable) {
        return
      }
      const rect = e.currentTarget.getBoundingClientRect()
      if (!await this.ensureLoaded()) {
        return
      }
      this.active = null
      // 画面の右端・下端からはみ出さないように
      const x = Math.min(rect.left, window.innerWidth - 340)
      const y = Math.min(rect.bottom + 4, window.innerHeight - 320)
      this.popover = { kind, type, week, x, y }
    },

    /**
     * 小窓を閉じる
     */
    closePopover () {
      this.popover = { kind: null, type: null, week: null, x: 0, y: 0 }
    },

    /**
     * 小窓の外を押したら閉じる
     */
    onDocumentDown (e) {
      if (this.popover.kind && this.$refs.popover && !this.$refs.popover.contains(e.target)) {
        this.closePopover()
      }
    },

    /**
     * 形態の付け外し。毎週・隔週はほかと一緒に選べない
     */
    toggleAtt (id) {
      const key = this.popover.type + this.popover.week
      let arr = this.work[key].slice()
      if (arr.includes(id)) {
        arr = arr.filter((a) => a !== id)
      } else if (['00', '10'].includes(id)) {
        arr = [id]
      } else {
        arr = arr.filter((a) => !['00', '10'].includes(a))
        arr.push(id)
      }
      // 形態の順（毎週・第1〜5・隔週）にそろえる
      arr.sort((a, b) => a.localeCompare(b))
      this.work[key] = arr
      if (!arr.length) {
        this.clearStaffAndExchange()
      }
      if (this.popover.type === 'clean') {
        this.setHolidayType(this.work)
      }
      this.mstDirty = true
    },

    /**
     * その曜日の形態を全部外す（担当者・振替先も外す）
     */
    clearAtt () {
      this.work[this.popover.type + this.popover.week] = []
      this.clearStaffAndExchange()
      if (this.popover.type === 'clean') {
        this.setHolidayType(this.work)
      }
      this.mstDirty = true
    },

    clearStaffAndExchange () {
      this.work[this.popover.type + 'Staff' + this.popover.week] = ''
      this.work[this.popover.type + 'StaffId' + this.popover.week] = null
      if (this.popover.type === 'clean') {
        this.work['exchange' + this.popover.week] = null
      }
    },

    /**
     * 実施月の付け外し
     */
    toggleMonth (m) {
      if (this.work.months.includes(m)) {
        this.work.months = this.work.months.filter((x) => x !== m)
      } else {
        this.work.months = this.work.months.concat([m]).sort((a, b) => a - b)
      }
      this.mstDirty = true
    },

    /**
     * 振替先を決める
     */
    setExchange (num) {
      this.work['exchange' + this.popover.week] = num
      this.mstDirty = true
      this.closePopover()
    },

    // ---------- 曜日のコピーと貼付 ----------

    /**
     * その曜日のゴミ出し・清掃の形態と担当者を控える（振替先は曜日ごとの事情が違うので控えない）
     */
    async copyWeek (w) {
      if (!await this.ensureLoaded()) {
        return
      }
      const c = {}
      for (const t of this.types) {
        c[t] = this.work[t + w.id].slice()
        c[t + 'Staff'] = this.work[t + 'Staff' + w.id]
        c[t + 'StaffId'] = this.work[t + 'StaffId' + w.id]
      }
      this.clip = c
      this.clipWeek = w.id
    },

    /**
     * 控えた形態と担当者をその曜日に入れる（保存は人が押す）。貼り付け先の振替先はそのまま
     */
    async pasteWeek (w) {
      if (!this.clip || this.clipWeek === w.id) {
        return
      }
      if (!await this.ensureLoaded()) {
        return
      }
      const c = this.clip
      for (const t of this.types) {
        this.work[t + w.id] = c[t].slice()
        this.work[t + 'Staff' + w.id] = c[t + 'Staff']
        this.work[t + 'StaffId' + w.id] = c[t + 'StaffId']
      }
      // 清掃が無くなったら振替先も外す
      if (!this.work['clean' + w.id].length) {
        this.work['exchange' + w.id] = null
      }
      this.setHolidayType(this.work)
      this.mstDirty = true
    },

    // ---------- 担当者（形態の小窓の中） ----------

    /**
     * 担当者を候補から選んだ（× で消したときは null）
     */
    staffSelected (e) {
      if (this.popover.kind !== 'att' || !this.work) {
        return
      }
      const type = this.popover.type
      const week = this.popover.week
      if (e) {
        this.work[type + 'Staff' + week] = e.userName
        this.work[type + 'StaffId' + week] = Number(e.userId)
      } else {
        this.work[type + 'Staff' + week] = ''
        this.work[type + 'StaffId' + week] = null
      }
      this.mstDirty = true
    },

    // ---------- 適用開始日 ----------

    async openStart () {
      if (!this.canEditStart) {
        return
      }
      if (!await this.ensureLoaded()) {
        return
      }
      if (this.works[0] !== this.work) {
        return
      }
      this.closePopover()
      this.active = 'start'
    },

    changeStart (val) {
      this.active = null
      if (!val || val === this.work.startDate) {
        return
      }
      // 1 つ前の区画より後の日付だけ
      if (this.works[1] && this.works[1].startDate && val <= this.works[1].startDate) {
        dialogs.showErrorDialog('適用開始日', `前の設定（${this.works[1].startDate} 〜）より後の日付にしてください。`)
        return
      }
      this.work.startDate = val
      this.mstDirty = true
    },

    // ---------- 区画 ----------

    async toggleExpand () {
      this.expanded = !this.expanded
      if (this.expanded) {
        if (!this.spec) {
          this.initSpec()
        }
        await this.ensureLoaded()
        if (!this.newStart) {
          this.newStart = this.newStartMin || moment().format('YYYY-MM-DD')
        }
      }
    },

    // ---------- 特別仕様 ----------

    /**
     * 特別仕様の編集中の値を prop から写す
     */
    initSpec () {
      const s = this.special
      const format = s && s.format ? s.format : ''
      this.spec = { format, items: this.specItems(format, s ? s.items : null) }
    },

    /**
     * 仕様の箇所の数にそろえた配列（{ key, name }）。足りない分は定数の初期値
     */
    specItems (format, items) {
      const def = RLS_SPECIAL_FORMATS[format]
      if (!def) {
        return []
      }
      const res = []
      for (let i = 0; i < def.itemCount; i++) {
        const name = items && items[i] != null ? items[i] : def.items[i] || ''
        res.push({ key: 'sp' + i + '_' + Math.random().toString(36).slice(2, 8), name })
      }
      return res
    },

    /**
     * 仕様を変えた（箇所は保存済みがあればそれ、無ければ初期値）
     */
    changeFormat () {
      const saved = this.special && this.special.format === this.spec.format ? this.special.items : null
      this.spec.items = this.specItems(this.spec.format, saved)
      this.specDirty = true
    },

    /**
     * 特別仕様を保存（仕様が「なし」なら削除）
     * API: rls/saveSpecial
     */
    async saveSpecial () {
      const items = this.spec.items.map((it) => (it.name || '').trim())
      await backend.postData('rls/saveSpecial', { siteCode: this.row.siteCode, format: this.spec.format, items, user: this.$store.getters.user.id })
      const saved = this.spec.format ? { siteCode: this.row.siteCode, format: this.spec.format, items } : null
      logManager.recording(this, logManager.Loglevel.INFO, 'RLSマスタ', '特別仕様', `${this.row.site} の特別仕様を「${saved ? RLS_SPECIAL_FORMATS[saved.format].name : 'なし'}」にしました。`)
      this.specDirty = false
      this.$emit('special', { siteCode: this.row.siteCode, special: saved })
    },

    /**
     * 今の内容を写して、新しい適用開始日の区画を先頭に足す
     */
    async addPeriod () {
      if (!this.newStart) {
        await dialogs.showErrorDialog('適用開始日', '新しい設定の適用開始日を入れてください。')
        return
      }
      if (this.newStartMin && this.newStart < this.newStartMin) {
        await dialogs.showErrorDialog('適用開始日', `今の設定（${this.works[0].startDate} 〜）より後の日付にしてください。`)
        return
      }
      const w = utils.clone(this.works[0])
      w.startDate = this.newStart
      w.endDate = null
      this.works = [w].concat(this.works)
      this.work = w
      this.structureChanged = true
      this.mstDirty = true
      this.expanded = false
      dialogs.showInfoDialog('新しい設定', `${this.newStart} からの設定を作りました。この行で直して「保存」を押してください。`)
    },

    /**
     * 将来の区画を消す（保存で確定）
     */
    async removePeriod (i) {
      const p = this.works[i]
      const r = await dialogs.showConfirmDialog('区画の削除', `${p.startDate} からの設定を削除しますか？（「保存」で確定します）`, 'はい', 'キャンセル')
      if (r !== 'YES') {
        return
      }
      this.works.splice(i, 1)
      this.structureChanged = true
      this.mstDirty = true
    },

    /**
     * 変更を捨てて読み直す
     */
    cancel () {
      const changed = this.structureChanged
      this.works = null
      this.work = null
      this.structureChanged = false
      this.mstDirty = false
      this.specDirty = false
      this.spec = null
      this.popover = { kind: null, type: null, week: null, x: 0, y: 0 }
      this.active = null
      if (this.expanded) {
        this.initSpec()
        this.ensureLoaded()
      }
      if (changed) {
        this.emitDirty(false)
        this.$emit('reload')
      }
    },

    // ---------- 祝日の自動判定 ----------

    /**
     * 清掃の曜日の数・形態から祝日の扱いを決める（1：振替、2：清掃なし）
     */
    setHolidayType (row) {
      let res = BigNumber(0)
      for (const week of WEEKS) {
        const tar = row['clean' + week.id]
        if (!tar || !tar.length) {
          continue
        }
        if (tar.includes('00')) {
          res = res.plus(4.3)
        } else if (tar.includes('10')) {
          res = res.plus(2.15)
        } else {
          for (const id of ['01', '02', '03', '04']) {
            if (tar.includes(id)) {
              res = res.plus(1)
            }
          }
          if (tar.includes('05')) {
            res = res.plus(0.34)
          }
        }
      }
      const n = Number(res)
      if (n === 0) {
        row.holiday = null
      } else if (n > 8.6) {
        // 週 2 回より多い → 祝日は清掃なし
        row.holiday = 2
      } else {
        row.holiday = 1
      }
    },

    // ---------- 保存 ----------

    /**
     * 固定休とかぶっていないか（今日以降に効く区画だけ）
     */
    async checkHolidayWeek (w, week, staffId) {
      if (w.endDate && w.endDate <= this.today) {
        return true
      }
      const staff = this.staffList.find((s) => Number(s.userId) === Number(staffId))
      if (staff && staff.holidayWeeks && staff.holidayWeeks.map(Number).includes(week.num)) {
        await dialogs.showErrorDialog(`${staff.userName}さんは${week.name}曜は休みです`, '固定休にかぶっています。\r\n曜日を変更するか、ユーザー設定で固定休の曜日を変更してください。')
        return false
      }
      return true
    },

    /**
     * 区画の入力チェック（保存前）
     */
    async validate (works) {
      for (let i = 0; i < works.length; i++) {
        const w = works[i]
        if (!w.startDate) {
          await dialogs.showErrorDialog('適用開始日を選択してください', '契約開始日が分からない場合は、今月1日でも大丈夫です。')
          return false
        }
        if (!w.months || !w.months.length) {
          await dialogs.showErrorDialog('実施月が未選択です', '実施月は1つ以上選択してください。')
          return false
        }
        this.setHolidayType(w)
        let any = false
        for (const week of WEEKS) {
          for (const t of this.types) {
            const arr = w[t + week.id]
            if (!arr.length) {
              w[t + 'StaffId' + week.id] = null
              w[t + 'Staff' + week.id] = ''
              continue
            }
            any = true
            if (arr.length > 1 && (arr.includes('00') || arr.includes('10'))) {
              await dialogs.showErrorDialog(`${t === 'trash' ? 'ゴミ出し' : '清掃'}の${week.name}曜日が不正です`, '「毎週」「隔週」はほかの値と一緒に選べません。')
              return false
            }
            if (w[t + 'StaffId' + week.id]) {
              if (!await this.checkHolidayWeek(w, week, w[t + 'StaffId' + week.id])) {
                return false
              }
            }
          }
          // 振替先
          if (!w['clean' + week.id].length || w.holiday !== 1) {
            w['exchange' + week.id] = null
          } else if (w['exchange' + week.id]) {
            const ex = WEEKS.find((x) => x.num === w['exchange' + week.id])
            if (ex && w['clean' + ex.id].includes('00')) {
              await dialogs.showErrorDialog(`${week.name}曜日の振替指定曜日`, `${ex.name}曜日は「毎週」なので振替先に指定できません。`)
              return false
            }
          }
        }
        if (!any) {
          await dialogs.showErrorDialog('ゴミ出し・清掃　未選択', `${w.startDate} からの設定は、どちらの曜日も未選択です。`)
          return false
        }
      }
      return true
    },

    /**
     * 保存。保存の直前に区画を取り直し、この行の区画だけ差し替えてから物件ごとに保存する
     * （同じ物件の別の区画の行を別に直していても消さないため）
     * API: rls/saveMst（保存後にその物件の予定を作り直す）
     */
    async save () {
      if (this.saving || !this.work) {
        return
      }
      this.saving = true
      this.closePopover()
      try {
        // 特別仕様だけ変えたときはマスタ（予定の作り直し）は触らない
        if (this.specDirty) {
          await this.saveSpecial()
        }
        if (!this.mstDirty) {
          this.savedMark = true
          setTimeout(() => { this.savedMark = false }, 3000)
          return
        }
        let works
        if (this.structureChanged) {
          // 区画を足した・消したときは、この行の持っている並びをそのまま使う
          works = this.works.map((w) => utils.clone(w))
        } else {
          const res = await backend.searchData('rls/getRlsMstBySiteCode', { siteCode: this.row.siteCode })
          const rows = (res.data && res.data.data) || []
          works = (rows.length > 1 || (rows[0] && rows[0].months)) ? rows.map((r) => toWork(r)) : []
          const mine = utils.clone(this.work)
          const idx = works.findIndex((w) => w.startDate === this.row.startDate)
          if (idx >= 0) {
            works[idx] = mine
          } else {
            works = [mine].concat(works)
          }
        }
        works.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
        // 終了日は次の区画の前日
        for (let i = 0; i < works.length; i++) {
          works[i].endDate = i === 0 ? null : moment(works[i - 1].startDate + 'T00:00:00').add(-1, 'days').format('YYYY-MM-DD')
        }
        if (!await this.validate(works)) {
          return
        }

        const payload = works.map((w) => {
          const p = { startDate: w.startDate, endDate: w.endDate, months: w.months.slice().sort((a, b) => a - b), holiday: w.holiday }
          for (const week of WEEKS) {
            for (const t of this.types) {
              p[t + week.id + 'Id'] = w[t + week.id]
              p[t + 'StaffId' + week.id] = w[t + 'StaffId' + week.id]
            }
            p['exchange' + week.id] = w['exchange' + week.id]
          }
          return p
        })
        await backend.postData('rls/saveMst', { siteCode: this.row.siteCode, site: this.row.site, works: payload, insertUser: this.$store.getters.user.id })
        logManager.recordingByRegist(this, !this.row.startDate, 'RLSマスタ', `${this.row.site} の情報`)

        this.works = works
        this.work = works.find((w) => w.startDate === this.work.startDate) || works[0]
        this.mstDirty = false
        this.presetMark = false
        this.savedMark = true
        setTimeout(() => { this.savedMark = false }, 3000)
        this.$emit('saved', this.row.siteCode)
        if (this.structureChanged || !this.row.startDate) {
          this.structureChanged = false
          this.emitDirty(false)
          this.$emit('reload')
        }
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '保存できませんでした。もう一度お願いします。\r\n' + e)
      } finally {
        this.saving = false
      }
    },

    /**
     * この物件のマスタを全部削除
     * API: rls/deleteMst
     */
    async deleteMst () {
      const r = await dialogs.showConfirmDialog('本当に削除しますか？', `${this.row.site} のマスタを削除してよろしいでしょうか？`, 'はい', 'キャンセル')
      if (r !== 'YES') {
        return
      }
      try {
        await backend.postData('rls/deleteMst', { siteCode: this.row.siteCode })
        logManager.recording(this, logManager.Loglevel.INFO, 'RLSマスタ', '削除', `${this.row.site} を削除しました。`)
        this.mstDirty = false
        this.specDirty = false
        this.emitDirty(false)
        this.$emit('reload')
      } catch (e) {
        await dialogs.showErrorDialog('エラー', '削除できませんでした。もう一度お願いします。')
      }
    }
  }
}
</script>

<style scoped>
/* 行 */
.row > td {
  padding: 12px 8px;
  vertical-align: top;
  border-bottom: 1px solid #eef2f7;
  white-space: nowrap;
}

.row:hover > td {
  background: #f8fafc !important;
}

.row-dirty > td,
.row-dirty:hover > td {
  background: #fffbeb !important;
}

.row-open > td {
  border-bottom: 0;
}

.row-more > td {
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
}

/* 物件 */
.site-cell {
  padding-left: 16px !important;
  white-space: normal !important;
  min-width: 250px;
}

.action-cell {
  padding-right: 12px !important;
  min-width: 120px;
}

/* 実施月・祝日・適用開始日は左右中央 */
.center-cell {
  text-align: center !important;
}

/* 保存：大きく・ネイビーで・光る輪を点滅（Tailwind が important なので色でなく影で動かす） */
.save-btn {
  width: 104px;
  padding: 10px 0;
  border: 0;
  border-radius: 10px;
  /* 見出しと同じネイビー（ユーザーの好み。ほかの画面の indigo にはしない） */
  background: #1e3a8a;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.1em;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.35);
  animation: save-glow 1.4s ease-in-out infinite;
}

.save-btn:hover {
  background: #172554;
}

@keyframes save-glow {
  0%, 100% { box-shadow: 0 4px 12px rgba(30, 58, 138, 0.35), 0 0 0 0 rgba(245, 158, 11, 0.7); }
  50% { box-shadow: 0 4px 12px rgba(30, 58, 138, 0.35), 0 0 0 8px rgba(245, 158, 11, 0); }
}

.cancel-btn {
  width: 104px;
  padding: 5px 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5563;
  font-size: 12px;
  cursor: pointer;
}

.cancel-btn:hover {
  background: #f3f4f6;
}

.site-name {
  font-weight: 700;
  font-size: 14px;
  margin-right: 8px;
}

.site-client {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  white-space: pre-line;
}

/* 札 */
.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.badge-yellow { color: #92400e; background: #fde68a; }
.badge-green { color: #065f46; background: #d1fae5; }
.badge-blue { color: #1e40af; background: #dbeafe; }
.badge-orange { color: #9a3412; background: #ffedd5; }
.badge-indigo { color: #1e3a8a; background: #e0e7ff; }
.badge-gray { color: #475569; background: #f1f5f9; }
.badge-pink { color: #9d174d; background: #fce7f3; }

/* 特別仕様 */
.spec-select {
  font-size: 13px;
  padding: 4px 28px 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
}

.spec-input {
  flex: 1;
  font-size: 13px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
}

.spec-input:focus {
  border-color: #4f46e5;
  outline: none;
}

.pill {
  font-size: 11px;
  font-weight: 700;
  color: #1e3a8a;
  border: 1px solid #c7d2fe;
  background: #ffffff;
  padding: 2px 10px;
  border-radius: 999px;
  cursor: pointer;
}

.pill:hover {
  background: #eef2ff;
}

/* 押せる欄 */
.box {
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px dashed transparent;
  font-weight: 500;
}

.box-edit {
  border-color: #c7d2fe;
  cursor: pointer;
}

.box-edit:hover {
  background: #eef2ff;
}

/* 曜日 */
.week-cell {
  border-left: 1px solid #eef2f7;
  min-width: 104px;
  padding: 8px 5px !important;
}

.chip {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  padding: 5px 6px;
  border: 1px solid;
  font-size: 12px;
}

.chip-trash {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.chip-clean {
  background: #f5f3ff;
  border-color: #ddd6fe;
  color: #6d28d9;
}

.chip-edit {
  cursor: pointer;
}

.chip.chip-edit:hover {
  border-color: #4f46e5;
}

.chip-att {
  font-weight: 700;
}

.chip-staff {
  margin-left: auto;
  font-size: 11px;
  color: #374151;
}

.chip-none {
  color: #b91c1c;
  font-weight: 700;
}

.chip-preset {
  border: 2px solid #f59e0b;
}

.chip-empty {
  height: 32px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 16px;
}

.chip-empty.chip-edit {
  border-color: #d1d5db;
}

.chip-empty.chip-edit:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.exchange {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  padding: 2px 4px;
  border-radius: 6px;
  border: 1px dashed transparent;
}

/* 曜日のコピー・貼付（同じ幅の小さなボタン） */
.cp-btn {
  width: 48px;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  color: #374151;
  background: #ffffff;
  border: 1px solid #9ca3af;
  border-radius: 999px;
  cursor: pointer;
}

.cp-btn:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.cp-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cp-src {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #3730a3;
  font-weight: 700;
}

/* その他の設定ボタン */
.more {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #3730a3;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 2px 8px;
  cursor: pointer;
  white-space: nowrap;
}

.more:hover,
.more-open {
  background: #e0e7ff;
}

/* その他の設定の中 */
.card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
}

.card-title {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}

.card-note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  white-space: normal;
}

.period {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  color: #4b5563;
}

.period-cur {
  background: #eef2ff;
  color: #1e3a8a;
  font-weight: 700;
}

.period-mark {
  font-size: 11px;
  background: #ffffff;
  padding: 1px 8px;
  border-radius: 999px;
}

.danger {
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #b91c1c;
  font-weight: 700;
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
}

.danger:hover {
  background: #fff1f2;
}

/* 小窓 */
.popover {
  position: fixed;
  z-index: 60;
  width: 320px;
  background: white;
  border: 1px solid #c7d2fe;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  padding: 12px;
  font-size: 13px;
  color: #374151;
  white-space: normal;
}

.pop-title {
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
}

.opt {
  text-align: center;
  padding: 8px 0;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #4b5563;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.opt:hover {
  border-color: #4f46e5;
}

.opt-on {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
}

.opt-off {
  border-color: #fecaca;
  background: #fff1f2;
  color: #b91c1c;
}

.opt:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
